import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useMediaUrl } from './useMediaSasToken';

interface OnboardingVideoProps {
  /** Path relative to the media container, e.g. "journey1/welcome.mp4" */
  videoPath: string;
  /** Path relative to the media container, e.g. "journey1/welcome.srt" */
  subtitlePath?: string;
  subtitleLang?: string;
  subtitleLabel?: string;
  height?: number;
}

export const OnboardingVideo = ({
  videoPath,
  subtitlePath,
  subtitleLang = 'pt',
  subtitleLabel = 'Português',
  height = 280,
}: OnboardingVideoProps) => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.type === 'dark';

  const { fetchMediaUrl, baseUrl } = useMediaUrl();

  const [videoUrl, setVideoUrl] = useState<string>('');
  const [subUrl, setSubUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!baseUrl) return;
    let cancelled = false;
    fetchMediaUrl(videoPath).then(url => { if (!cancelled) setVideoUrl(url); }).catch(() => { if (!cancelled) setError(true); });
    if (subtitlePath) {
      fetchMediaUrl(subtitlePath).then(url => { if (!cancelled) setSubUrl(url); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [baseUrl, videoPath, subtitlePath, fetchMediaUrl]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <Box
        style={{
          height,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <Typography variant="caption" style={{ color: '#999' }}>
          Video unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ marginBottom: 16, position: 'relative', height }}>
      {(loading || !videoUrl) && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1a1a1a',
            borderRadius: 8,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      {videoUrl && (
        <video
          controls
          onLoadedData={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
          style={{
            width: '100%',
            height,
            borderRadius: 8,
            backgroundColor: isDarkTheme ? '#424242' : '#fff',
            opacity: loading ? 0 : 1,
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          {subUrl && (
            <track
              kind="subtitles"
              src={subUrl}
              srcLang={subtitleLang}
              label={subtitleLabel}
            />
          )}
        </video>
      )}
    </Box>
  );
};
